import React from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { Removable } from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { useRetrieveConstraints } from "@planzone/view-models";
import { ConstraintsPopup } from "@common/constraintsPopup";
import { useModal } from "@root/client/modal";
import { RichTextInput } from "@common/richTextInput";
import { Props } from "./types";
import { AllocationsList } from "./allocationsList";
import { Hierarchy } from "./hierarchy";
import { TagList } from "./tagList";
import styles from "./styles.scss";

function Task({ task, project, displayAllocationMenu, displayTagMenu }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { constraints } = useRetrieveConstraints();
  const isLate = task.endDate && new Date(task.endDate) < new Date();
  const isDone =
    task.percentComplete === 100 || task.status.percentComplete === 100;
  const elementConstraints = constraints.filter(
    ({ targetEntityUuid, originEntityUuid }) =>
      targetEntityUuid === task.uuid || originEntityUuid === task.uuid,
  );
  const isConstrained = elementConstraints.length > 0;

  return (
    <div
      className={classnames(styles.task, {
        [styles.taskSpace]: task.tags.length > 0,
        [styles.done]: isDone,
        [styles.late]: isLate,
        [styles.constrained]: isConstrained,
      })}
      onClick={() => pushView({ key: UseCases.retrieveTask, data: { task } })}
    >
      <div className={styles.header}>
        <div className={styles.hierarchy}>
          <Hierarchy list={task.list} />
        </div>
        {isConstrained && (
          <div
            className={styles.constraintsPopup}
            onClick={(e) => e.stopPropagation()}
          >
            <ConstraintsPopup
              elementId={task.uuid}
              elementType="task"
              constraintsNbr={elementConstraints.length}
              project={project}
              portal
            />
          </div>
        )}
        <AllocationsList
          allocations={task.allocations}
          task={task}
          displayAllocationMenu={displayAllocationMenu}
        />
      </div>
      <h3 className={styles.name}>{task.name}</h3>
      <div
        className={classnames(styles.dates, {
          [styles.late]: isLate && !isDone,
        })}
      >
        <span className={styles.dateLabel}>{t("kanban.task.startDate")} :</span>
        <span>
          {task.startDate ? new Date(task.startDate).toLocaleDateString() : "-"}
        </span>
        <span className={styles.dateLabel}>{t("kanban.task.endDate")} :</span>
        <span>
          {task.endDate ? new Date(task.endDate).toLocaleDateString() : "-"}
        </span>
      </div>
      {task.description && (
        <RichTextInput
          value={task.description}
          isDisabled
          input={() => {}}
          className={styles.description}
        />
      )}
      <div className={styles.tagList}>
        <TagList
          project={project}
          task={task}
          displayTagMenu={displayTagMenu}
          removableId={task.uuid}
          removable={Removable.TASK}
        />
      </div>
    </div>
  );
}

export { Task };
